import React, { useState } from 'react';
import { Users, Loader2 } from 'lucide-react';
import { fetchFamilyTree } from './api/familyTreeApi.ts';
import FamilyTreeNode from './components/FamilyTreeNode';
import SearchBar from './components/SearchBar';

const FamilyTree = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [familyData, setFamilyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [suggestions] = useState([
    "Queen Victoria",
    "Napoleon Bonaparte",
    "Ashoka the Great",
    "Shivaji Maharaj",
  ]);

  const handleFetchFamilyTree = async (query) => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchFamilyTree(query);
      setFamilyData(data);
    } catch (err) {
      setError(err.message || 'Failed to load family tree. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    handleFetchFamilyTree(searchQuery);
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchQuery(suggestion);
    handleFetchFamilyTree(suggestion);
  };

  return (
    <div className="min-h-screen bg-[#f4e4bc] pt-16 font-serif">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <Users className="h-16 w-16 text-[#8b4513]" />
          </div>
          <h1
            className="text-4xl font-extrabold text-[#8b4513] sm:text-5xl md:text-6xl"
            style={{ textShadow: '2px 2px 4px rgba(139, 69, 19, 0.2)' }}
          >
            Royal Family Trees
          </h1>
          <p className="mt-3 max-w-md mx-auto text-base text-[#5c2e0a] sm:text-lg md:mt-5 md:text-xl md:max-w-3xl">
            Explore the genealogy of history's most influential dynasties
          </p>
        </div>

        <SearchBar 
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          handleSearch={handleSearch}
          suggestions={suggestions}
          handleSuggestionClick={handleSuggestionClick}
        />

        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-12 w-12 text-[#8b4513] animate-spin" />
          </div>
        )}

        {error && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-8 rounded-lg">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {familyData && !loading && (
          <div
            className="bg-[#fffbf0] rounded-xl shadow-lg p-6 border-4 border-[#8b4513] overflow-x-auto"
            style={{ boxShadow: '8px 8px 0px rgba(139, 69, 19, 0.2)' }}
          >
            <div className="min-w-[800px] py-4">
              <div className="grid grid-cols-1 gap-8">
                {familyData.parents.length > 0 && (
                  <div>
                    <h2 className="text-xl font-bold text-[#8b4513] mb-4">Parents</h2>
                    <div className="grid grid-cols-2 gap-4">
                      {familyData.parents.map((parent, index) => (
                        <FamilyTreeNode key={index} member={parent} relationshipType="parent" />
                      ))}
                    </div>
                  </div>
                )}

                {familyData.spouses.length > 0 && (
                  <div>
                    <h2 className="text-xl font-bold text-[#8b4513] mb-4">Spouses</h2>
                    <div className="grid grid-cols-2 gap-4">
                      {familyData.spouses.map((spouse, index) => (
                        <FamilyTreeNode key={index} member={spouse} relationshipType="spouse" />
                      ))}
                    </div>
                  </div>
                )}

                {familyData.children.length > 0 && (
                  <div>
                    <h2 className="text-xl font-bold text-[#8b4513] mb-4">Children</h2>
                    <div className="grid grid-cols-2 gap-4">
                      {familyData.children.map((child, index) => (
                        <FamilyTreeNode key={index} member={child} relationshipType="child" />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FamilyTree;
